import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Quote, Minus, Link as LinkIcon, Image as ImageIcon, Undo, Redo,
} from "lucide-react";

interface Props {
  value: string;
  onChange: (html: string) => void;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

const PostEditor = ({ value, onChange }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // Disable defaults we don't allow
        codeBlock: false,
        code: false,
        strike: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: false }),
      Placeholder.configure({ placeholder: "Skriv ditt nyhetsbrev här…" }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-article dark:prose-invert max-w-none min-h-[400px] focus:outline-none px-4 py-3",
      },
      handleDrop: (_view, event) => {
        const dt = (event as DragEvent).dataTransfer;
        const files = dt?.files ? Array.from(dt.files) : [];
        const images = files.filter((f) => f.type.startsWith("image/"));
        if (images.length === 0) return false;
        event.preventDefault();
        images.forEach((f) => void uploadImage(f));
        return true;
      },
      handlePaste: (_view, event) => {
        const items = (event as ClipboardEvent).clipboardData?.items;
        if (!items) return false;
        const images: File[] = [];
        for (const it of Array.from(items)) {
          if (it.kind === "file") {
            const f = it.getAsFile();
            if (f && f.type.startsWith("image/")) images.push(f);
          }
        }
        if (images.length === 0) return false;
        event.preventDefault();
        images.forEach((f) => void uploadImage(f));
        return true;
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Sync external value changes (e.g. when loading post)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const uploadImage = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Not an image", description: file.name, variant: "destructive" });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast({ title: "Image too large", description: "Max 10 MB.", variant: "destructive" });
      return;
    }
    const ext = file.name.split(".").pop() || "jpg";
    const path = `body/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("post-covers")
      .upload(path, file, { cacheControl: "3600", upsert: false });
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      return;
    }
    // Long-lived signed URL (10 years) — bucket is private per workspace policy.
    const { data, error: signErr } = await supabase.storage
      .from("post-covers")
      .createSignedUrl(path, 60 * 60 * 24 * 365 * 10);
    if (signErr || !data) {
      toast({ title: "Could not sign image URL", description: signErr?.message, variant: "destructive" });
      return;
    }
    editor.chain().focus().setImage({ src: data.signedUrl, alt: file.name }).run();
  };

  const onPickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) uploadImage(f);
    e.target.value = "";
  };

  const Btn = ({
    onClick, active, disabled, children, label,
  }: {
    onClick: () => void; active?: boolean; disabled?: boolean; children: React.ReactNode; label: string;
  }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`p-1.5 rounded hover:bg-muted transition-colors disabled:opacity-40 ${
        active ? "bg-muted text-primary" : "text-foreground/80"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-md border border-input bg-background">
      <div className="sticky top-0 z-10 bg-background flex flex-wrap items-center gap-0.5 border-b border-border p-1.5 rounded-t-md">

        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} label="Bold">
          <Bold className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} label="Italic">
          <Italic className="w-4 h-4" />
        </Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Heading 2">
          <Heading2 className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Heading 3">
          <Heading3 className="w-4 h-4" />
        </Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Bullet list">
          <List className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Ordered list">
          <ListOrdered className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Quote">
          <Quote className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Divider">
          <Minus className="w-4 h-4" />
        </Btn>
        <div className="w-px h-5 bg-border mx-1" />
        <Btn onClick={setLink} active={editor.isActive("link")} label="Link">
          <LinkIcon className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => fileInputRef.current?.click()} label="Insert image">
          <ImageIcon className="w-4 h-4" />
        </Btn>
        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onPickImage} />
        <div className="flex-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} label="Undo">
          <Undo className="w-4 h-4" />
        </Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} label="Redo">
          <Redo className="w-4 h-4" />
        </Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default PostEditor;
